import moment from "moment-timezone";
import type {
  ServicePackagePrefill,
  State as SettingPackageState,
} from "react-lib/apps/HISV3/HCU/sequence/SettingPackage";
import {
  canCreateServicePackage,
  SERVICE_PACKAGE_MENU,
  ServicePackageAccessState,
} from "../servicePackageAccess";
import ProductServiceKindListView from "issara-sdk/apis/ProductServiceKindListView_core";
import ProductServiceKindCreateView from "issara-sdk/apis/ProductServiceKindCreateView_core";
import ProductServiceKindDetailView from "issara-sdk/apis/ProductServiceKindDetailView_core";
import ProductServiceListCreateAPIView from "issara-sdk/apis/ProductServiceListCreateAPIView_core";
import ProductServiceRetrieveUpdateAPIView from "issara-sdk/apis/ProductServiceRetrieveUpdateAPIView_core";
import PricingDetailAPIView from "issara-sdk/apis/PricingDetailAPIView_apps_BIL";
import PricingListCreateAPIView from "issara-sdk/apis/PricingListCreateAPIView_apps_BIL";
import ProductTypeList from "issara-sdk/apis/ProductTypeList_core";
import ProductImageList from "issara-sdk/apis/ProductImageList_core";
import IngredientList from "issara-sdk/apis/IngredientList_apps_TPD";
import HelperLabelList from "issara-sdk/apis/HelperLabelList_apps_TPD";
import ManufacturerList from "issara-sdk/apis/ManufacturerList_apps_MSD";
import CentralLabUnitList from "issara-sdk/apis/CentralLabUnitList_apps_LAB";
import serviceCatalogApi from "./serviceCatalogApi";
import MasterMap from "react-lib/apps/HISV3/common/MasterMap";
import { parseDate, formatADtoBEString } from "react-lib/utils/dateUtils";
import {
  FieldSchema,
  Metadata,
  SelectOption,
  childMetadata,
  getFields,
  pricingFields,
  serviceMetadata,
  normalizeTextList,
  isFieldEnabled,
} from "../serviceKindSchema";

export const CARD = "CardSettingService";
export const LIMIT = 10;
export const buttonKey = (action: string) => `ServiceCatalog_${action}`;
export type RecordData = Record<string, any>;
export type CatalogFilter = {
  code?: string;
  name?: string;
  active_flag?: string;
  scope?: "mine" | "central" | "all";
};
export type RemoteFieldState = {
  options: SelectOption[];
  knownOptions: SelectOption[];
  loading?: boolean;
  error?: string;
  hint?: string;
};
export type CatalogForm = {
  key: number;
  kind: string;
  division?: any;
  packagePrefill?: ServicePackagePrefill;
  values: RecordData;
  original: RecordData | null;
  metadata: Metadata;
  pricingMetadata: Metadata;
  pricings: RecordData[];
  selectedPricing?: any;
  pricingErrors?: RecordData;
  pricingTableError?: string;
  pricingBusy?: boolean;
  readOnly: boolean;
  errors: RecordData;
  tab: number;
  addingPricing: boolean;
  remoteFields?: Record<string, RemoteFieldState>;
  productImages?: RecordData[];
  productImagesError?: string;
};
export type CatalogState = {
  sequenceIndex?: "Action" | null;
  division?: any;
  filter?: CatalogFilter;
  serviceList?: RecordData[];
  activePage?: number;
  total?: number;
  listMetadata?: Metadata;
  kinds?: { kind: string; label: string; enabled: boolean }[];
  masters?: Record<string, SelectOption[]>;
  masterErrors?: Record<string, string>;
  form?: CatalogForm | null;
};
export type State = { ServiceCatalogSequence?: CatalogState | null };
export const StateInitial: State = { ServiceCatalogSequence: null };
type SharedState = State &
  SettingPackageState &
  ServicePackageAccessState & {
    goToAdminMenu?: string;
    selectedDivision?: RecordData;
    buttonLoadCheck?: RecordData;
    errorMessage?: RecordData;
    successMessage?: RecordData;
  };
export type Controller = {
  apiToken: any;
  data: { division?: any; userProfile?: { is_superuser?: boolean } };
  getState: () => SharedState;
  setState: (
    update: Partial<SharedState> | ((state: SharedState) => Partial<SharedState>),
    callback?: () => void
  ) => void;
};
export type ActionParams = {
  sequence?: "ServiceCatalog";
  action:
    | "GET_KINDS"
    | "GET_KIND_OPTIONS"
    | "SEARCH_SERVICE"
    | "OPEN_SERVICE_FORM"
    | "SAVE_SERVICE"
    | "ADD_PRICING"
    | "DELETE_PRICING"
    | "CLOSE_SERVICE_FORM"
    | "SELECT_KIND"
    | "SEARCH_RELATED_SERVICES";
  kind?: string;
  editingPricing?: { id: any; values: RecordData };
  pricingId?: any;
  field?: string;
  search?: string;
  item?: RecordData;
  values?: RecordData;
  pricings?: RecordData[];
  noPrice?: boolean;
  image?: File | null;
  filter?: CatalogFilter;
  activePage?: number;
};

const cache = new WeakMap<
  Controller,
  { session: any; division: any; values: Map<string, Promise<any>> }
>();
const remoteRequests = new WeakMap<Controller, Map<string, symbol>>();
const searchRequests = new WeakMap<Controller, symbol>();
const formRequests = new WeakMap<Controller, symbol>();
let formKey = 0;
const update = (controller: Controller, changes: Partial<CatalogState>) =>
  controller.setState((state) => ({
    ServiceCatalogSequence: { ...state.ServiceCatalogSequence, ...changes },
  }));
const busy = (controller: Controller, action: string, status: string) =>
  controller.setState((state) => ({
    buttonLoadCheck: { ...state.buttonLoadCheck, [buttonKey(action)]: status },
  }));
const messages = (controller: Controller, error: any = null, success: any = null) =>
  controller.setState((state) => ({
    errorMessage: { ...state.errorMessage, [CARD]: error },
    successMessage: { ...state.successMessage, [CARD]: success },
  }));
export const errorData = (error: any): RecordData => {
  const data = error?.responseData ?? error?.response?.data ?? error;
  return data && typeof data === "object" && !(data instanceof Error)
    ? data
    : { non_field_errors: [String(data?.message || data || "ไม่สามารถติดต่อระบบได้")] };
};
export const errorText = (error: any, includeFieldNames = true): string => {
  if (Array.isArray(error))
    return error
      .map((value) => errorText(value, includeFieldNames))
      .filter(Boolean)
      .join(" · ");
  if (error && typeof error === "object")
    return Object.entries(error)
      .map(
        ([key, value]) =>
          `${!includeFieldNames || key === "non_field_errors" ? "" : key + ": "}${errorText(
            value,
            includeFieldNames
          )}`
      )
      .join(" · ");
  const text = error == null ? "" : String(error);
  const translations: Record<string, string> = {
    '"Start Date" should be newer than or equal to "today"': "วันที่เริ่มต้นต้องไม่ก่อนวันนี้",
    '"End Date" should be newer than or equal to "Start Date"':
      "วันที่สิ้นสุดต้องไม่ก่อนวันที่เริ่มต้น",
  };
  if (Object.prototype.hasOwnProperty.call(translations, text)) return translations[text];
  const overlap = /^Pricing overlap with (\d{4}-\d{2}-\d{2}) - (\d{4}-\d{2}-\d{2})$/.exec(text);
  return overlap && overlap.slice(1).every((date) => moment(date, "YYYY-MM-DD", true).isValid())
    ? `ช่วงราคาทับกับช่วง ${formatADtoBEString(overlap[1])} - ${formatADtoBEString(overlap[2])}`
    : text;
};
export const pricingErrorSummary = (errors: RecordData = {}, fields: FieldSchema[] = []) => {
  const keys = new Set(fields.map((field) => field.key));
  return Array.from(
    new Set(
      Object.entries(errors)
        .filter(([key]) => key === "non_field_errors" || !keys.has(key))
        .map(([, value]) => errorText(value, false))
        .filter(Boolean)
    )
  ).join(" · ");
};

const auth = (controller: Controller) => ({
  apiToken: controller.apiToken,
  extra: { division: controller.data.division },
});
const cached = async <T>(
  controller: Controller,
  key: string,
  load: () => Promise<T>
): Promise<T> => {
  let session = cache.get(controller);
  if (
    !session ||
    session.session !== controller.apiToken ||
    session.division !== controller.data.division
  ) {
    session = {
      session: controller.apiToken,
      division: controller.data.division,
      values: new Map(),
    };
    cache.set(controller, session);
  }
  if (!session.values.has(key)) {
    const pending = load().catch((error) => {
      session?.values.delete(key);
      throw error;
    });
    session.values.set(key, pending);
  }
  return session.values.get(key)!;
};
const resultOf = async (request: Promise<any>) => {
  const [response, error] = await request;
  if (error || response == null) throw error || new Error("ไม่พบข้อมูลจากระบบ");
  return response;
};
const listItems = (response: any): RecordData[] =>
  Array.isArray(response) ? response : response.items || response.results || [];
export const formatServiceEndDate = (date: any) =>
  date === "9999-12-31" ? "ไม่มีกำหนด" : formatADtoBEString(date);
export const todayISO = () => moment.tz("Asia/Bangkok").format("YYYY-MM-DD");
export const pricingStatus = (pricing: RecordData, today = todayISO()) => {
  if (pricing.end_date < today) return "หมดอายุ";
  if (pricing.start_date > today) return "ล่วงหน้า";
  return "ใช้อยู่";
};
export const defaultPricingId = (pricings: RecordData[]) => {
  const current = pricings.find((row) => pricingStatus(row) === "ใช้อยู่");
  if (current) return current.id;
  return pricings
    .filter((row) => pricingStatus(row) === "ล่วงหน้า")
    .sort((a, b) => a.start_date.localeCompare(b.start_date))[0]?.id;
};

export const stripDivisionPrefix = (code: string = "", division: any) => {
  const id = typeof division === "object" ? division?.id : division;
  const prefix = id == null ? "" : `${id}_`;
  return prefix && code.startsWith(prefix) ? code.slice(prefix.length) : code;
};
export const isEditable = (item: RecordData, division: any) => {
  if (typeof item.is_editable === "boolean") return item.is_editable;
  return String(item.of_division?.id ?? item.of_division ?? "") === String(division ?? "");
};
const idValue = (value: any) => (value && typeof value === "object" ? value.id : value);
export const normalizeValues = (data: RecordData, fields: FieldSchema[]): RecordData => {
  const values = { ...data };
  fields.forEach((field) => {
    if (field.input === "select" || (field.input === "remoteSelect" && !field.multiple))
      values[field.key] = idValue(data[field.key]);
    if (field.input === "multiselect" || (field.input === "remoteSelect" && field.multiple))
      values[field.key] = (data[field.key] || []).map(idValue);
    if (field.fields)
      values[field.key] = (data[field.key] || []).map((row: RecordData) =>
        normalizeValues(row, field.fields!)
      );
  });
  return values;
};
export const isoDate = (value: string) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = parseDate(value, true);
  return date?.isValid() ? date.format("YYYY-MM-DD") : value;
};
const cleanFields = (
  fields: FieldSchema[],
  values: RecordData,
  metadata: Metadata,
  creating = false
): RecordData => {
  const result: RecordData = {};
  fields.forEach((field) => {
    const meta = metadata[field.key];
    if (
      !meta ||
      !isFieldEnabled(field) ||
      field.readOnly ||
      field.uiOnly ||
      meta.read_only ||
      values[field.key] === undefined
    )
      return;
    let value = values[field.key];
    if (creating && field.ignoreDefault && (value === "" || value == null)) return;
    if (field.input === "textList") value = normalizeTextList(value);
    if (field.fields)
      value = (value || []).map((row: RecordData) =>
        cleanFields(field.fields!, row, childMetadata(meta), creating)
      );
    if (field.input === "date" && value) value = isoDate(value);
    if (
      (field.input === "select" ||
        field.input === "remoteSelect" ||
        field.input === "number" ||
        field.input === "decimal") &&
      value === "" &&
      meta.allow_null
    )
      value = null;
    result[field.key] = value;
  });
  return result;
};
export const buildServicePayload = (
  form: CatalogForm,
  values: RecordData,
  pricings: RecordData[],
  noPrice: boolean
) => {
  const payload = cleanFields(
    getFields(form.kind, form.metadata),
    values,
    form.metadata,
    !form.original
  );
  delete payload.extra;
  if (form.original) {
    ["code", "of_division", "price", "start_date", "end_date", "full_pricings"].forEach(
      (key) => delete payload[key]
    );
    const previous = cleanFields(getFields(form.kind, form.metadata), form.original, form.metadata);
    Object.keys(payload).forEach((key) => {
      if (JSON.stringify(payload[key]) === JSON.stringify(previous[key])) delete payload[key];
    });
  } else {
    payload.full_pricings = noPrice
      ? []
      : pricings.map((row) => cleanFields(pricingFields, row, form.pricingMetadata));
  }
  return payload;
};

export const pricingValues = (row: RecordData = {}): RecordData => {
  const values = normalizeValues(row, pricingFields);
  return Object.fromEntries(pricingFields.map((field) => [field.key, values[field.key] ?? ""]));
};
export const buildPricingPatch = (
  row: RecordData,
  values: RecordData,
  metadata: Metadata
): RecordData => {
  const next = cleanFields(pricingFields, values, metadata);
  const previous = cleanFields(pricingFields, pricingValues(row), metadata);
  return Object.fromEntries(
    Object.entries(next).filter(([key, value]) => {
      const before = previous[key];
      if (
        key.startsWith("price_") &&
        value !== "" &&
        value != null &&
        before !== "" &&
        before != null
      )
        return Number(value) !== Number(before);
      return JSON.stringify(value) !== JSON.stringify(before);
    })
  );
};
const pricingMutationBusy = (controller: Controller) =>
  ["SAVE_SERVICE", "ADD_PRICING", "DELETE_PRICING"].some(
    (action) => controller.getState().buttonLoadCheck?.[buttonKey(action)] === "LOADING"
  );
const pricingFormError = (controller: Controller, form: CatalogForm, error: any) => {
  const errors = errorData(error);
  update(controller, { form: { ...form, pricingErrors: errors, tab: 1, addingPricing: false } });
  messages(controller, errorText(errors, false));
};

const extraMasters: Record<
  string,
  {
    api: (args: any) => Promise<any>;
    params: RecordData;
    valueKey: string;
    textKey: string;
    codeKey?: string;
  }
> = {
  catalogLabOrgan: {
    api: serviceCatalogApi.listOrgans,
    params: {},
    valueKey: "id",
    textKey: "name",
  },
  catalogDrugType: {
    api: serviceCatalogApi.listDrugTypes,
    params: {},
    valueKey: "id",
    textKey: "name",
  },
  catalogSpecialLabel: {
    api: serviceCatalogApi.listSpecialLabels,
    params: {},
    valueKey: "id",
    textKey: "text",
  },
  catalogProductType: {
    api: ProductTypeList.list,
    params: {},
    valueKey: "id",
    textKey: "name",
    codeKey: "code",
  },
  catalogHelperLabel: { api: HelperLabelList.list, params: {}, valueKey: "id", textKey: "text" },
  catalogLabUnit: { api: CentralLabUnitList.list, params: {}, valueKey: "id", textKey: "name" },
};
const loadMasters = async (controller: Controller, fields: FieldSchema[], metadata: Metadata) => {
  const requestOptions = auth(controller);
  const keys = new Set<string>();
  const collect = (list: FieldSchema[], meta: Metadata) =>
    list.forEach((field) => {
      if (isFieldEnabled(field) && meta[field.key]) {
        if (field.options && "master" in field.options) keys.add(field.options.master);
        if (field.fields) collect(field.fields, childMetadata(meta[field.key]));
      }
    });
  collect(fields, metadata);
  const masterKeys = Array.from(keys);
  const results = await Promise.allSettled(
    masterKeys.map(async (key) => {
      const options = await cached<SelectOption[]>(controller, `master:${key}`, async () => {
        const source = extraMasters[key] || (MasterMap as Record<string, any>)[key];
        if (!source) throw new Error(`ไม่พบแหล่งข้อมูล ${key}`);
        let offset = 0;
        const items: RecordData[] = [];
        while (true) {
          const response = await resultOf(
            source.api({ ...requestOptions, params: { ...source.params, limit: 2000, offset } })
          );
          const page = listItems(response);
          items.push(...page);
          offset += page.length;
          if (
            !page.length ||
            Array.isArray(response) ||
            offset >= (response.total ?? response.count ?? offset)
          )
            break;
        }
        return items.map((item) => ({
          key: item[source.valueKey],
          value: item[source.valueKey],
          ...((source.codeKey || key === "billMode") &&
          item[source.codeKey || "code"] != null &&
          item[source.codeKey || "code"] !== ""
            ? { code: String(item[source.codeKey || "code"]) }
            : {}),
          text: String(
            item[source.textKey] ||
              item.label ||
              item.text ||
              item.name ||
              item.code ||
              item[source.valueKey]
          ),
        }));
      });
      return options;
    })
  );
  const masters: Record<string, SelectOption[]> = {};
  const masterErrors: Record<string, string> = {};
  results.forEach((result, index) => {
    const key = masterKeys[index];
    if (result.status === "fulfilled") {
      masters[key] = result.value;
    } else {
      masters[key] = [];
      masterErrors[key] = "โหลดตัวเลือกไม่สำเร็จ กรุณาปิดแล้วเปิดฟอร์มเพื่อลองอีกครั้ง";
    }
  });
  return { masters, masterErrors };
};
export const HandleGetKindOptions = async (controller: Controller, kind: string) =>
  cached<Metadata>(controller, `options:${kind}`, async () => {
    const response = await resultOf(
      kind === "GENERIC"
        ? serviceCatalogApi.serviceOptions(auth(controller))
        : serviceCatalogApi.kindOptions({ ...auth(controller), kind })
    );
    if (!response.actions?.POST) throw new Error("ไม่พบข้อมูลฟิลด์จาก OPTIONS");
    return response.actions.POST;
  });
export const HandleGetKinds = async (controller: Controller) => {
  const kinds = await cached(controller, "kinds", () =>
    resultOf(ProductServiceKindListView.get(auth(controller)))
  );
  if (!Array.isArray(kinds)) throw new Error("รูปแบบข้อมูลประเภทบริการไม่ถูกต้อง");
  return kinds;
};

export const HandleSearchService = async (
  controller: Controller,
  params: ActionParams
): Promise<void> => {
  const division = controller.data.division;
  const token = controller.apiToken;
  const previous = controller.getState().ServiceCatalogSequence;
  const filter =
    params.filter ??
    (previous?.division === division ? previous?.filter : { scope: "mine", active_flag: "ALL" });
  const activePage = Math.max(1, params.activePage ?? 1);
  const request = Symbol();
  searchRequests.set(controller, request);
  update(controller, {
    division,
    filter,
    activePage,
    serviceList: [],
    total: 0,
    sequenceIndex: "Action",
  });
  const current = () =>
    searchRequests.get(controller) === request &&
    controller.data.division === division &&
    controller.apiToken === token &&
    controller.getState().ServiceCatalogSequence?.division === division;
  busy(controller, "SEARCH_SERVICE", "LOADING");
  try {
    const { scope, active_flag, ...search } = filter || {};
    const [response, listMetadata] = await Promise.all([
      resultOf(
        ProductServiceListCreateAPIView.list({
          ...auth(controller),
          params: {
            ...search,
            ...(active_flag && active_flag !== "ALL" ? { active_flag } : {}),
            ...(scope !== "all" ? { of_division: scope === "central" ? "null" : division } : {}),
            limit: LIMIT,
            offset: (activePage - 1) * LIMIT,
          },
        })
      ),
      HandleGetKindOptions(controller, "GENERIC"),
    ]);
    if (!current()) return;
    if (!Array.isArray(response.items) || !Number.isFinite(response.total))
      throw new Error("รูปแบบข้อมูลบริการไม่ถูกต้อง");
    const lastPage = Math.max(1, Math.ceil(response.total / LIMIT));
    if (activePage > lastPage)
      return HandleSearchService(controller, { ...params, filter, activePage: lastPage });
    update(controller, { serviceList: response.items, total: response.total, listMetadata });
    busy(controller, "SEARCH_SERVICE", "SUCCESS");
  } catch (error) {
    if (!current()) return;
    busy(controller, "SEARCH_SERVICE", "ERROR");
    messages(controller, errorText(errorData(error)));
  }
};

const mergeOptions = (...lists: SelectOption[][]): SelectOption[] =>
  Array.from(new Map(lists.flat().map((option) => [option.value, option])).values());
const remoteFieldEntries = (
  fields: FieldSchema[],
  prefix = ""
): { field: FieldSchema; key: string }[] =>
  fields.flatMap((field) => {
    const key = prefix ? `${prefix}.${field.key}` : field.key;
    return field.input === "remoteSelect"
      ? [{ field, key }]
      : remoteFieldEntries(field.fields || [], key);
  });
const resolveRemoteFields = async (
  controller: Controller,
  fields: FieldSchema[],
  original: RecordData | null
) => {
  const requests = new Map<any, Promise<any>>();
  const entries = await Promise.all(
    remoteFieldEntries(fields).map(async ({ field, key }) => {
      if (field.remote?.source) {
        const parents = key
          .split(".")
          .slice(0, -1)
          .reduce<RecordData[]>(
            (rows, parent) => rows.flatMap((row) => row[parent] || []),
            [original || {}]
          );
        const displayKey =
          field.remote.source === "ingredient" ? "ingredient_name" : "manufacturer_display";
        const knownOptions = mergeOptions(
          parents
            .filter((row) => row[field.key] != null && row[field.key] !== "")
            .map((row) => ({
              key: row[field.key],
              value: row[field.key],
              text: row[displayKey]?.trim() || "ไม่สามารถโหลดชื่อรายการที่เลือก",
            }))
        );
        return [key, { options: [], knownOptions, loading: false }] as const;
      }
      const value = original?.[field.key];
      const ids: any[] = value == null ? [] : Array.isArray(value) ? value : [value];
      const results = await Promise.allSettled(
        ids.map((id) => {
          if (!requests.has(id))
            requests.set(
              id,
              resultOf(
                ProductServiceRetrieveUpdateAPIView.retrieve({ ...auth(controller), pk: id })
              )
            );
          return requests.get(id)!;
        })
      );
      const knownOptions = results.map((result, index) =>
        result.status === "fulfilled"
          ? { key: ids[index], value: ids[index], text: result.value.name || result.value.code }
          : { key: ids[index], value: ids[index], text: "ไม่สามารถโหลดชื่อรายการที่เลือก" }
      );
      const state: RemoteFieldState = {
        options: [],
        knownOptions,
        loading: false,
        ...(results.some((result) => result.status === "rejected") && {
          error: "โหลดชื่อรายการเดิมไม่สำเร็จ กรุณาเปิดฟอร์มเพื่อลองใหม่",
        }),
      };
      return [key, state] as const;
    })
  );
  return Object.fromEntries(entries);
};
const updateRemoteField = (
  controller: Controller,
  field: string,
  changes: Partial<RemoteFieldState>
) =>
  controller.setState((state) => {
    const form = state.ServiceCatalogSequence?.form;
    if (!form) return {};
    return {
      ServiceCatalogSequence: {
        ...state.ServiceCatalogSequence,
        form: {
          ...form,
          remoteFields: {
            ...form.remoteFields,
            [field]: { options: [], knownOptions: [], ...form.remoteFields?.[field], ...changes },
          },
        },
      },
    };
  });
export const HandleSearchRelatedServices = async (controller: Controller, params: ActionParams) => {
  const form = controller.getState().ServiceCatalogSequence?.form;
  if (!form || form.readOnly) return;
  const entry = remoteFieldEntries(getFields(form.kind, form.metadata)).find(
    ({ key }) => key === params.field
  );
  if (!entry?.field.remote) return;
  const { field, key } = entry;
  const remote = field.remote!;
  const source = remote.source;
  const query = (params.search || "").trim();
  const division = controller.data.division;
  if (!source && division == null) {
    updateRemoteField(controller, key, {
      error: "กรุณาเลือกหน่วยงานก่อนค้นหา",
      loading: false,
    });
    return;
  }
  const requests = remoteRequests.get(controller) || new Map<string, symbol>();
  remoteRequests.set(controller, requests);
  const request = Symbol();
  requests.set(key, request);
  const validForm = formIsCurrent(controller, form);
  const current = () => validForm() && requests.get(key) === request;
  if (source && !query) {
    updateRemoteField(controller, key, {
      options: [],
      loading: false,
      error: undefined,
      hint: undefined,
    });
    return;
  }
  updateRemoteField(controller, key, { loading: true, error: undefined, hint: undefined });
  try {
    const response = await resultOf(
      source
        ? (source === "ingredient" ? IngredientList : ManufacturerList).list({
            ...auth(controller),
            params: { name__icontains: query, limit: 20, offset: 0 },
          })
        : ProductServiceListCreateAPIView.list({
            ...auth(controller),
            params: {
              kind: remote.kind,
              name: query,
              limit: remote.editableOnly ? 50 : 20,
              offset: 0,
            },
          })
    );
    if (!current()) return;
    const options = listItems(response)
      .filter(
        (item) =>
          source ||
          ((!remote.excludeSelf || item.id !== form.original?.id) &&
            (!item.kind || item.kind === remote.kind) &&
            (!remote.editableOnly || item.is_editable === true))
      )
      .map((item) => ({ key: item.id, value: item.id, text: item.name || item.code }));
    const previous = controller.getState().ServiceCatalogSequence?.form?.remoteFields?.[key];
    updateRemoteField(controller, key, {
      options,
      knownOptions: mergeOptions(previous?.knownOptions || [], options),
      loading: false,
      hint:
        !source && remote.editableOnly && !options.length ? "พิมพ์ชื่อให้เจาะจงขึ้น" : undefined,
    });
  } catch (error) {
    if (!current()) return;
    updateRemoteField(controller, key, {
      options: [],
      loading: false,
      error: errorText(errorData(error)),
    });
  }
};

export const HandleOpenServiceForm = async (controller: Controller, params: ActionParams) => {
  const request = Symbol();
  formRequests.set(controller, request);
  const division = controller.data.division;
  const token = controller.apiToken;
  const current = () =>
    formRequests.get(controller) === request &&
    controller.data.division === division &&
    controller.apiToken === token;
  busy(controller, "OPEN_SERVICE_FORM", "LOADING");
  messages(controller);
  update(controller, { form: null });
  try {
    const kind = params.item?.kind || params.kind;
    if (!kind) {
      const kinds = await HandleGetKinds(controller);
      if (current()) update(controller, { kinds });
    } else {
      const [kindMetadata, pricingOptions, detail] = await Promise.all([
        HandleGetKindOptions(controller, kind),
        cached<Metadata>(controller, "pricing-options", async () => {
          const response = await resultOf(serviceCatalogApi.pricingOptions(auth(controller)));
          if (!response.actions?.POST) throw new Error("ไม่พบข้อมูลราคาจาก OPTIONS");
          return response.actions.POST;
        }),
        params.item
          ? resultOf(
              kind === "GENERIC"
                ? ProductServiceRetrieveUpdateAPIView.retrieve({
                    ...auth(controller),
                    pk: params.item.id,
                  })
                : ProductServiceKindDetailView.retrieve({
                    ...auth(controller),
                    kind,
                    pk: params.item.id,
                  })
            )
          : Promise.resolve(null),
      ]);
      if (!current()) return;
      const metadata = serviceMetadata(kindMetadata);
      const pricingMetadata = detail ? pricingOptions : childMetadata(metadata.full_pricings);
      const fields = getFields(kind, metadata);
      const original = detail ? normalizeValues(detail, fields) : null;
      const values = original
        ? { ...original }
        : Object.fromEntries(
            fields
              .filter(
                (field) =>
                  (!field.ignoreDefault && metadata[field.key]?.default !== undefined) ||
                  field.defaultValue !== undefined ||
                  field.input === "checkbox"
              )
              .map((field) => [
                field.key,
                !field.ignoreDefault && metadata[field.key]?.default !== undefined
                  ? metadata[field.key].default
                  : field.defaultValue ?? false,
              ])
          );
      if (kind === "LAB") {
        values.is_child_lab = !!values.parent_lab;
        const names = values.alternative_names || [];
        values.alternative_names = names.length ? [...names] : Array(original ? 1 : 3).fill("");
      }
      const [masterData, pricingResponse, remoteFields, productImages] = await Promise.all([
        loadMasters(controller, [...fields, ...pricingFields], { ...metadata, ...pricingMetadata }),
        detail
          ? resultOf(
              PricingListCreateAPIView.list({
                ...auth(controller),
                params: { code: detail.code, limit: 999999 },
              })
            )
          : Promise.resolve([]),
        resolveRemoteFields(controller, fields, values),
        detail && kind === "DRUG"
          ? resultOf(
              ProductImageList.list({
                ...auth(controller),
                product_id: detail.id,
                params: { limit: 999999 },
              })
            ).then(
              (response) => ({
                items: listItems(response).filter((image) => image.image || image.thumbnail),
                error: undefined,
              }),
              () => ({
                items: [],
                error: "โหลดรูปยาไม่สำเร็จ กรุณาปิดแล้วเปิดฟอร์มเพื่อลองอีกครั้ง",
              })
            )
          : Promise.resolve({ items: [], error: undefined }),
      ]);
      if (!current()) return;
      const pricings = listItems(pricingResponse);
      const selectedPricing = defaultPricingId(pricings);
      update(controller, {
        masters: {
          ...controller.getState().ServiceCatalogSequence?.masters,
          ...masterData.masters,
        },
        masterErrors: masterData.masterErrors,
        form: {
          key: ++formKey,
          kind,
          division,
          values,
          original,
          metadata,
          remoteFields,
          productImages: productImages.items,
          productImagesError: productImages.error,
          pricingMetadata,
          pricings,
          selectedPricing,
          readOnly: detail
            ? params.item?.is_editable === false || !isEditable(detail, division)
            : false,
          errors: {},
          tab: 0,
          addingPricing: false,
        },
      });
    }
    if (current()) busy(controller, "OPEN_SERVICE_FORM", "SUCCESS");
  } catch (error) {
    if (!current()) return;
    busy(controller, "OPEN_SERVICE_FORM", "ERROR");
    messages(controller, errorText(errorData(error)));
  }
};

const formIsCurrent = (controller: Controller, form: CatalogForm) => {
  const division = controller.data.division;
  const token = controller.apiToken;
  return () =>
    controller.data.division === division &&
    controller.apiToken === token &&
    controller.getState().ServiceCatalogSequence?.form?.key === form.key;
};
const relationError = (value: any): any => {
  if (Array.isArray(value)) return value.map(relationError);
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value).map(([key, error]) => [key, relationError(error)])
    );
  return typeof value === "string" && /^Invalid pk "[^"]+" - object does not exist\.$/.test(value)
    ? "เลือกได้เฉพาะรายการที่หน่วยงานนี้แก้ไขได้"
    : value;
};
const formError = (controller: Controller, form: CatalogForm, error: any, pricingOnly = false) => {
  const errors = { ...errorData(error) };
  ["parent_lab", "limited_solvents"].forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(errors, key)) errors[key] = relationError(errors[key]);
  });
  update(controller, {
    form: {
      ...form,
      errors,
      tab:
        pricingOnly ||
        (errors.full_pricings && !Object.keys(errors).some((key) => key !== "full_pricings"))
          ? 1
          : 0,
    },
  });
  messages(controller, errorText(errors, !pricingOnly));
};
export const HandleSaveService = async (controller: Controller, params: ActionParams) => {
  const form = controller.getState().ServiceCatalogSequence?.form;
  if (!form || form.readOnly || pricingMutationBusy(controller)) return;
  const current = formIsCurrent(controller, form);
  busy(controller, "SAVE_SERVICE", "LOADING");
  messages(controller);
  let savedForm = form;
  let savingPricing = false;
  let pricingSaved = false;
  let uploadingImage = false;
  try {
    if (params.editingPricing && form.original) {
      savingPricing = true;
      const row = form.pricings.find((row) => row.id === params.editingPricing!.id);
      if (!row) throw { non_field_errors: ["ไม่พบช่วงราคาที่เลือก"] };
      const patch = buildPricingPatch(row, params.editingPricing.values, form.pricingMetadata);
      if (Object.keys(patch).length) {
        const status = pricingStatus(row);
        if (status === "หมดอายุ") throw { non_field_errors: ["ช่วงราคาที่หมดอายุแล้วแก้ไขไม่ได้"] };
        if (status === "ใช้อยู่" && patch.start_date !== undefined)
          throw { start_date: ["แก้ไขวันที่เริ่มต้นของช่วงราคาที่ใช้อยู่ไม่ได้"] };
        const updated = await resultOf(
          PricingDetailAPIView.patch({ ...auth(controller), pk: row.id, data: patch })
        );
        if (!current()) return;
        const replacement = {
          ...row,
          ...patch,
          ...(patch.bill_mode !== undefined && {
            bill_mode_name: undefined,
            bill_mode_code: undefined,
          }),
          ...updated,
        };
        savedForm = {
          ...form,
          pricingErrors: {},
          pricingTableError: undefined,
          pricings: form.pricings.map((item) => (item.id === row.id ? replacement : item)),
        };
        update(controller, { form: savedForm });
        pricingSaved = true;
      }
      savingPricing = false;
    }
    const data = buildServicePayload(
      form,
      params.values || {},
      params.pricings || [],
      !!params.noPrice
    );
    const response = form.original
      ? Object.keys(data).length
        ? await resultOf(
            form.kind === "GENERIC"
              ? ProductServiceRetrieveUpdateAPIView.patch({
                  ...auth(controller),
                  pk: form.original.id,
                  data,
                })
              : ProductServiceKindDetailView.patch({
                  ...auth(controller),
                  kind: form.kind,
                  pk: form.original.id,
                  data,
                })
          )
        : form.original
      : await resultOf(
          ProductServiceKindCreateView.create({ ...auth(controller), kind: form.kind, data })
        );
    if (!current()) return;
    const createPackage =
      ((!form.original && params.values?.create_package) || form.packagePrefill) &&
      canCreateServicePackage(controller);
    const firstPricing = form.packagePrefill || data.full_pricings?.[0] || {};
    const pendingPrefill: ServicePackagePrefill | undefined = createPackage
      ? {
          serviceId: response.id ?? form.original?.id,
          serviceCode: response.code ?? form.original?.code,
          kind: form.kind,
          division: controller.data.division,
          name: response.name ?? data.name ?? form.packagePrefill?.name ?? "",
          name_en: response.name_en ?? data.name_en ?? form.packagePrefill?.name_en ?? "",
          start_date: firstPricing.start_date ?? "",
          end_date: firstPricing.end_date ?? "",
          price_normal: firstPricing.price_normal ?? "",
          price_premium: firstPricing.price_premium ?? "",
          price_foreign: firstPricing.price_foreign ?? "",
        }
      : undefined;
    if (params.image && form.kind === "DRUG") {
      const original = normalizeValues(response, getFields(form.kind, form.metadata));
      savedForm = {
        ...savedForm,
        original,
        values: original,
        packagePrefill: pendingPrefill,
        errors: {},
        pricings: form.original ? savedForm.pricings : [],
        key: form.key,
      };
      update(controller, { form: savedForm });
      const image = new FormData();
      image.append("image", params.image);
      image.append("is_hero", "true");
      uploadingImage = true;
      await resultOf(
        ProductImageList.create({
          ...auth(controller),
          product_id: response.id,
          data: image as any,
        })
      );
      if (!current()) return;
      uploadingImage = false;
    }
    if (pendingPrefill) {
      controller.setState((state) => ({
        ServiceCatalogSequence: { ...state.ServiceCatalogSequence, form: null },
        SettingPackageSequence: {
          ...state.SettingPackageSequence,
          showDetail: false,
          pendingPrefill,
        },
        goToAdminMenu: SERVICE_PACKAGE_MENU,
      }));
    } else update(controller, { form: null });
    busy(controller, "SAVE_SERVICE", "SUCCESS");
    messages(controller, null, "บันทึกข้อมูลสำเร็จ");
    await HandleSearchService(controller, {
      action: "SEARCH_SERVICE",
      activePage: controller.getState().ServiceCatalogSequence?.activePage,
    });
  } catch (error) {
    if (!current()) return;
    busy(controller, "SAVE_SERVICE", "ERROR");
    if (savingPricing) {
      pricingFormError(controller, form, error);
    } else if (uploadingImage) {
      const message = `บันทึกข้อมูลบริการแล้ว แต่อัปโหลดรูปไม่สำเร็จ: ${errorText(
        errorData(error)
      )}`;
      if (!form.original) {
        try {
          const response = await resultOf(
            PricingListCreateAPIView.list({
              ...auth(controller),
              params: { code: savedForm.original!.code, limit: 999999 },
            })
          );
          if (!current()) return;
          savedForm = { ...savedForm, pricings: listItems(response) };
        } catch {
          if (!current()) return;
        }
      }
      formError(controller, savedForm, { product_image: [message] });
    } else {
      formError(
        controller,
        savedForm,
        pricingSaved
          ? {
              ...errorData(error),
              non_field_errors: [
                `บันทึกช่วงราคาแล้ว แต่บันทึกข้อมูลบริการไม่สำเร็จ: ${errorText(errorData(error))}`,
              ],
            }
          : error
      );
    }
  }
};
export const HandleAddPricing = async (controller: Controller, params: ActionParams) => {
  const form = controller.getState().ServiceCatalogSequence?.form;
  if (!form?.original || form.readOnly || pricingMutationBusy(controller)) return;
  const current = formIsCurrent(controller, form);
  busy(controller, "ADD_PRICING", "LOADING");
  messages(controller);
  let savedForm: CatalogForm | null = null;
  try {
    const data = {
      ...cleanFields(pricingFields, params.values || {}, form.pricingMetadata),
      code: form.original.code,
    };
    const created = await resultOf(PricingListCreateAPIView.create({ ...auth(controller), data }));
    if (!current()) return;
    const pricings = [...form.pricings, { ...data, ...created }];
    savedForm = {
      ...form,
      errors: {},
      pricings,
      addingPricing: false,
      selectedPricing: defaultPricingId(pricings),
    };
    update(controller, { form: savedForm });
    const response = await resultOf(
      PricingListCreateAPIView.list({
        ...auth(controller),
        params: { code: form.original.code, limit: 999999 },
      })
    );
    if (!current()) return;
    const refreshed = listItems(response);
    update(controller, {
      form: {
        ...savedForm,
        pricings: refreshed,
        selectedPricing: defaultPricingId(refreshed),
      },
    });
    busy(controller, "ADD_PRICING", "SUCCESS");
    messages(controller, null, "เพิ่มราคาใหม่สำเร็จ");
    await HandleSearchService(controller, {
      action: "SEARCH_SERVICE",
      activePage: controller.getState().ServiceCatalogSequence?.activePage,
    });
  } catch (error) {
    if (!current()) return;
    if (savedForm) {
      busy(controller, "ADD_PRICING", "SUCCESS");
      const message = `บันทึกราคาใหม่แล้ว แต่โหลดรายการราคาไม่สำเร็จ: ${errorText(
        errorData(error)
      )}`;
      formError(controller, savedForm, { non_field_errors: [message] }, true);
    } else {
      busy(controller, "ADD_PRICING", "ERROR");
      formError(controller, form, error, true);
    }
  }
};

export const HandleDeletePricing = async (controller: Controller, params: ActionParams) => {
  const form = controller.getState().ServiceCatalogSequence?.form;
  if (!form?.original || form.readOnly || pricingMutationBusy(controller)) return;
  const row = form.pricings.find((row) => row.id === params.pricingId);
  if (!row || pricingStatus(row) !== "ล่วงหน้า") return;
  const current = formIsCurrent(controller, form);
  busy(controller, "DELETE_PRICING", "LOADING");
  update(controller, { form: { ...form, pricingBusy: true, pricingTableError: undefined } });
  let savedForm: CatalogForm | undefined;
  try {
    const [, error] = await serviceCatalogApi.deletePricing({ ...auth(controller), pk: row.id });
    if (error) throw error;
    if (!current()) return;
    const remaining = form.pricings.filter((item) => item.id !== row.id);
    const selectedPricing =
      form.selectedPricing === row.id ? defaultPricingId(remaining) : form.selectedPricing;
    savedForm = {
      ...form,
      pricings: remaining,
      selectedPricing,
      pricingBusy: true,
      pricingTableError: undefined,
      pricingErrors: {},
    };
    update(controller, { form: savedForm });
    const response = await resultOf(
      PricingListCreateAPIView.list({
        ...auth(controller),
        params: { code: form.original.code, limit: 999999 },
      })
    );
    if (!current()) return;
    const refreshed = listItems(response);
    update(controller, {
      form: {
        ...savedForm,
        pricings: refreshed,
        pricingBusy: false,
        selectedPricing:
          form.selectedPricing === row.id ? defaultPricingId(refreshed) : savedForm.selectedPricing,
      },
    });
    busy(controller, "DELETE_PRICING", "SUCCESS");
    messages(controller, null, "ลบช่วงราคาแล้ว");
  } catch (error) {
    if (!current()) return;
    busy(controller, "DELETE_PRICING", savedForm ? "SUCCESS" : "ERROR");
    update(controller, {
      form: {
        ...(savedForm || form),
        pricingBusy: false,
        pricingTableError: `${
          savedForm ? "ลบช่วงราคาแล้ว แต่โหลดรายการราคาไม่สำเร็จ: " : ""
        }${errorText(errorData(error), false)}`,
      },
    });
  }
};

export const Start = async (controller: Controller, params: ActionParams) => {
  update(controller, { sequenceIndex: "Action" });
  return Action(controller, params);
};
export const Action = async (controller: Controller, params: ActionParams) => {
  switch (params.action) {
    case "SEARCH_RELATED_SERVICES":
      return HandleSearchRelatedServices(controller, params);
    case "SEARCH_SERVICE":
      return HandleSearchService(controller, params);
    case "GET_KINDS":
    case "SELECT_KIND":
      return HandleOpenServiceForm(controller, { ...params, kind: undefined, item: undefined });
    case "GET_KIND_OPTIONS":
    case "OPEN_SERVICE_FORM":
      return HandleOpenServiceForm(controller, params);
    case "SAVE_SERVICE":
      return HandleSaveService(controller, params);
    case "ADD_PRICING":
      return HandleAddPricing(controller, params);
    case "DELETE_PRICING":
      return HandleDeletePricing(controller, params);
    case "CLOSE_SERVICE_FORM":
      formRequests.set(controller, Symbol());
      update(controller, { form: null });
      ["OPEN_SERVICE_FORM", "SAVE_SERVICE", "ADD_PRICING", "DELETE_PRICING"].forEach((action) =>
        busy(controller, action, "SUCCESS")
      );
      return;
  }
};
